// HKDSE數學數據 - HKDSE Mathematics Data
// 系統化設計，準備後續 Supabase 數據遷移
export const hkdseMathsData = {
  title: 'HKDSE 數學學習系統',
  description: '全面的HKDSE數學學習資源，包含所有課題和練習',
  
  topics: [
    {
      id: 'hkdse-m01',
      code: 'M01',
      title: '數系與近似值',
      titleEn: 'Number system & Approximation',
      description: '學習各種數系的性質和近似值的計算方法',
      difficulty: 'basic',
      estimatedTime: '2-3週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '實數的分類和性質',
        '有效數字的概念',
        '四捨五入和截取',
        '科學記數法',
        '誤差的計算'
      ],
      exercises: [
        {
          id: 'ex-m01-1',
          title: '實數分類練習',
          type: 'classification',
          question: '將下列數字分類為自然數、整數、有理數或無理數：',
          items: [
            { number: '5', answer: '自然數、整數、有理數' },
            { number: '-3', answer: '整數、有理數' },
            { number: '\\frac{2}{3}', answer: '有理數' },
            { number: '\\sqrt{2}', answer: '無理數' },
            { number: '\\pi', answer: '無理數' }
          ]
        },
        {
          id: 'ex-m01-2',
          title: '近似值計算',
          type: 'calculation',
          question: '將 $3.14159$ 四捨五入至指定位數：',
          subQuestions: [
            { instruction: '保留兩位小數', answer: '3.14' },
            { instruction: '保留三位小數', answer: '3.142' },
            { instruction: '保留四位小數', answer: '3.1416' }
          ]
        }
      ],
      pastPapers: [
        {
          year: 2024,
          questionNumber: 1,
          marks: 4,
          question: '計算 $\\sqrt{50}$ 的值，答案以最簡根式表示。',
          solution: '$\\sqrt{50} = \\sqrt{25 \\times 2} = 5\\sqrt{2}$'
        },
        {
          year: 2023,
          questionNumber: 2,
          marks: 3,
          question: '將 $0.000456$ 以科學記數法表示。',
          solution: '$0.000456 = 4.56 \\times 10^{-4}$'
        }
      ]
    },
    {
      id: 'hkdse-m02',
      code: 'M02',
      title: '恆等式',
      titleEn: 'Identities',
      description: '學習代數恆等式的性質和應用',
      difficulty: 'basic',
      estimatedTime: '3-4週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '基本恆等式',
        '完全平方公式',
        '平方差公式',
        '立方和差公式',
        '三角恆等式'
      ],
      exercises: [
        {
          id: 'ex-m02-1',
          title: '恆等式展開',
          type: 'expansion',
          question: '展開下列恆等式：',
          items: [
            { expression: '(x + 3)^2', answer: 'x^2 + 6x + 9' },
            { expression: '(2a - b)^2', answer: '4a^2 - 4ab + b^2' },
            { expression: '(x + y)(x - y)', answer: 'x^2 - y^2' }
          ]
        },
        {
          id: 'ex-m02-2',
          title: '因式分解',
          type: 'factorization',
          question: '因式分解下列表達式：',
          items: [
            { expression: 'x^2 - 16', answer: '(x + 4)(x - 4)' },
            { expression: '4x^2 + 12x + 9', answer: '(2x + 3)^2' },
            { expression: 'a^3 - 8', answer: '(a - 2)(a^2 + 2a + 4)' }
          ]
        }
      ],
      pastPapers: [
        {
          year: 2024,
          questionNumber: 3,
          marks: 5,
          question: '因式分解 $x^3 - 6x^2 + 9x$',
          solution: '$x^3 - 6x^2 + 9x = x(x^2 - 6x + 9) = x(x - 3)^2$'
        }
      ]
    },
    {
      id: 'hkdse-m03',
      code: 'M03',
      title: '聯立二元一次方程',
      titleEn: 'Simultaneous linear equations',
      description: '學習解聯立二元一次方程的各種方法',
      difficulty: 'intermediate',
      estimatedTime: '2-3週',
      hasExerciseSystem: true, // 練習系統已完成
      keyPoints: [
        '代入法',
        '消元法',
        '圖解法',
        '應用題解法',
        '特殊情況分析'
      ],
      exercises: [
        {
          id: 'ex-m03-1',
          title: '代入法練習',
          type: 'substitution',
          question: '用代入法解下列聯立方程：',
          system: {
            equation1: 'x + y = 5',
            equation2: '2x - y = 1'
          },
          solution: 'x = 2, y = 3'
        },
        {
          id: 'ex-m03-2',
          title: '消元法練習',
          type: 'elimination',
          question: '用消元法解下列聯立方程：',
          system: {
            equation1: '3x + 2y = 12',
            equation2: '2x - y = 1'
          },
          solution: 'x = 2, y = 3'
        }
      ],
      pastPapers: [
        {
          year: 2024,
          questionNumber: 4,
          marks: 6,
          question: '解聯立方程：$2x + 3y = 11$, $x - y = 1$',
          solution: '代入法：$x = 1 + y$，代入第一個方程得 $y = 3$，$x = 4$'
        }
      ]
    },
    // 新增 M04-M23 模組
    {
      id: 'hkdse-m04',
      code: 'M04',
      title: '角、平行線、三角形與四邊形',
      titleEn: 'Angles, parallel lines, triangles and quadrilaterals',
      description: '學習基本幾何圖形的性質和角度關係',
      difficulty: 'basic',
      estimatedTime: '3-4週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '角的性質和分類',
        '平行線的性質',
        '三角形的性質',
        '四邊形的性質',
        '全等和相似'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m05',
      code: 'M05',
      title: '百分法',
      titleEn: 'Percentages',
      description: '學習百分比的計算和應用',
      difficulty: 'basic',
      estimatedTime: '2-3週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '百分比的概念',
        '百分比與分數的轉換',
        '百分比的計算',
        '增減百分比',
        '複合百分比'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m06',
      code: 'M06',
      title: '變換與對稱',
      titleEn: 'Transformation & Symmetry',
      description: '學習幾何圖形的變換和對稱性質',
      difficulty: 'intermediate',
      estimatedTime: '3-4週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '平移變換',
        '反射變換',
        '旋轉變換',
        '縮放變換',
        '對稱性質'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m07',
      code: 'M07',
      title: '指數、指數函數與對數函數',
      titleEn: 'Indices, Exponential and logarithmic functions',
      description: '學習指數和對數的性質和應用',
      difficulty: 'advanced',
      estimatedTime: '4-5週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '指數律',
        '指數函數',
        '對數的定義',
        '對數函數',
        '指數和對數方程'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m08',
      code: 'M08',
      title: '二次方程與二次函數圖像',
      titleEn: 'Quadratic equations & Quadratic graphs',
      description: '學習二次方程的解法和二次函數的圖像',
      difficulty: 'intermediate',
      estimatedTime: '4-5週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '二次方程的解法',
        '判別式',
        '二次函數圖像',
        '頂點形式',
        '應用問題'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m09',
      code: 'M09',
      title: '多項式、餘式及因式定理',
      titleEn: 'Polynomials, Remainder and factor theorem',
      description: '學習多項式的性質和定理',
      difficulty: 'advanced',
      estimatedTime: '3-4週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '多項式的基本概念',
        '多項式除法',
        '餘式定理',
        '因式定理',
        '多項式方程'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m10',
      code: 'M10',
      title: '不等式與線性規劃',
      titleEn: 'Inequalities and linear programming',
      description: '學習不等式的解法和線性規劃',
      difficulty: 'intermediate',
      estimatedTime: '3-4週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '一元不等式',
        '二元不等式',
        '線性規劃',
        '可行域',
        '最優解'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m11',
      code: 'M11',
      title: '率、比與變分',
      titleEn: 'Rates, ratios and variations',
      description: '學習比率和變分的概念及應用',
      difficulty: 'basic',
      estimatedTime: '2-3週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '比和比例',
        '正比例',
        '反比例',
        '聯比例',
        '變分應用'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m12',
      code: 'M12',
      title: '等差數列與等比數列',
      titleEn: 'Arithmetic and geometric sequences',
      description: '學習數列的性質和求和公式',
      difficulty: 'intermediate',
      estimatedTime: '3-4週',
      hasExerciseSystem: true, // 已實現練習系統
      keyPoints: [
        '等差數列',
        '等差數列求和',
        '等比數列',
        '等比數列求和',
        '無窮等比級數'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m13',
      code: 'M13',
      title: '圓的性質',
      titleEn: 'Properties of circles',
      description: '學習圓的幾何性質和定理',
      difficulty: 'intermediate',
      estimatedTime: '3-4週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '圓的基本性質',
        '弦的性質',
        '切線的性質',
        '圓周角定理',
        '相交弦定理'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m14',
      code: 'M14',
      title: '求積法',
      titleEn: 'Mensuration',
      description: '學習各種圖形的面積和體積計算',
      difficulty: 'basic',
      estimatedTime: '2-3週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '平面圖形面積',
        '立體圖形體積',
        '表面積計算',
        '複合圖形',
        '應用問題'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m15',
      code: 'M15',
      title: '三角學',
      titleEn: 'Trigonometry',
      description: '學習三角函數的性質和應用',
      difficulty: 'intermediate',
      estimatedTime: '4-5週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '三角函數定義',
        '三角恆等式',
        '三角方程',
        '三角函數圖像',
        '複合角公式'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m16',
      code: 'M16',
      title: '三角學於二維及三維空間問題的應用',
      titleEn: 'Application of trigonometry in 2D and 3D problems',
      description: '學習三角學在空間問題中的應用',
      difficulty: 'advanced',
      estimatedTime: '3-4週',
      hasExerciseSystem: false,
      keyPoints: [
        '二維三角測量',
        '三維空間角度',
        '方位角和仰角',
        '距離計算',
        '實際應用問題'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m17',
      code: 'M17',
      title: '直線方程',
      titleEn: 'Equations of straight lines',
      description: '學習直線方程的各種形式和性質',
      difficulty: 'intermediate',
      estimatedTime: '3-4週',
      hasExerciseSystem: false,
      keyPoints: [
        '直線的斜率',
        '點斜式',
        '截距式',
        '一般式',
        '平行和垂直直線'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m18',
      code: 'M18',
      title: '圓方程',
      titleEn: 'Equations of circles',
      description: '學習圓的方程和相關性質',
      difficulty: 'intermediate',
      estimatedTime: '2-3週',
      hasExerciseSystem: false,
      keyPoints: [
        '圓的標準方程',
        '圓的一般方程',
        '直線與圓的關係',
        '圓與圓的關係',
        '切線方程'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m19',
      code: 'M19',
      title: '軌跡',
      titleEn: 'Locus',
      description: '學習點的軌跡和相關問題',
      difficulty: 'advanced',
      estimatedTime: '3-4週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '軌跡的定義',
        '基本軌跡',
        '複合軌跡',
        '參數方程',
        '軌跡應用'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m20',
      code: 'M20',
      title: '排列與組合',
      titleEn: 'Permutations and combinations',
      description: '學習計數原理和排列組合',
      difficulty: 'intermediate',
      estimatedTime: '3-4週',
      hasExerciseSystem: false,
      keyPoints: [
        '乘法原理',
        '排列',
        '組合',
        '重複排列',
        '應用問題'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m21',
      code: 'M21',
      title: '概率',
      titleEn: 'Probability',
      description: '學習概率的基本概念和計算',
      difficulty: 'intermediate',
      estimatedTime: '4-5週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '概率的定義',
        '加法定理',
        '乘法定理',
        '條件概率',
        '貝葉斯定理'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m22',
      code: 'M22',
      title: '統計學',
      titleEn: 'Statistics',
      description: '學習統計學的基本概念和方法',
      difficulty: 'intermediate',
      estimatedTime: '4-5週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '數據的收集和整理',
        '中心趨勢',
        '離散程度',
        '正態分佈',
        '假設檢驗'
      ],
      exercises: [],
      pastPapers: []
    },
    {
      id: 'hkdse-m23',
      code: 'M23',
      title: '跨課題練習',
      titleEn: 'Cross-topic problems',
      description: '綜合各課題的練習和應用',
      difficulty: 'advanced',
      estimatedTime: '2-3週',
      hasExerciseSystem: true, // 標記有練習系統
      keyPoints: [
        '跨課題整合',
        '綜合應用',
        '解題策略',
        '實際問題',
        '考試準備'
      ],
      exercises: [],
      pastPapers: []
    }
  ],
  
  // 練習系統支援狀態
  exerciseSystemStatus: {
    supported: ['M01', 'M02', 'M03', 'M04', 'M05', 'M06', 'M07', 'M08', 'M09', 'M10', 'M11', 'M12', 'M13', 'M14', 'M15', 'M19', 'M21', 'M22', 'M23'],
    inDevelopment: [],
    planned: ['M16', 'M17', 'M18', 'M20']
  },
  
  // 統計信息
  statistics: {
    totalModules: 23,
    basicLevel: 5,
    intermediateLevel: 12,
    advancedLevel: 6,
    withExerciseSystem: 19
  }
};

export const hkdseMathsCategories = [
  { id: 'all', name: '全部課題', color: 'bg-gray-500' },
  { id: 'basic', name: '基礎課題', color: 'bg-green-500' },
  { id: 'intermediate', name: '中級課題', color: 'bg-yellow-500' },
  { id: 'advanced', name: '高級課題', color: 'bg-red-500' }
];

export default hkdseMathsData; 